import {
  AfterViewInit,
  Component,
  ContentChild,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MatTableDataSource } from '@angular/material/table';
import {
  BaseFpxDataService,
  ColumnType,
  CriteriaQuery,
  IColumnArray,
  SearchType,
  SortType,
  ToolBar,
  RoGridService,
  CellActionArray,
  RowActionArray
} from '@fpx/core';
import { debounce, debounceTime, map, Observable, of, Subject, take, takeLast, takeUntil, takeWhile } from 'rxjs';
import { FormControlTemplatesService } from 'src/app/templates/form-control-templates.service';

@Component({
  selector: 'fpx-ro-grid',
  templateUrl: './fpx-ro-grid.component.html',
  styleUrls: ['./fpx-ro-grid.component.scss'],
})
export class FpxRoGridComponent implements OnInit, OnDestroy {
  public mattabledataSource!: MatTableDataSource<any>;
  displayedColumns!: Array<string | any>;
  public displaycolumndefs!: ColumnDefinition[];
  @Input() searchPlaceholder: string = '';
  @Input() toolBarConfig: ToolBar[] = [];
  @Input() columnsArray!: IColumnArray<any>[];
  @Input() tableDatafn!: BaseFpxDataService<any>;
  @Input() title: string = '';
  @Input() gridWidth!: number;
  @Input() gridHeight!: number;
  @Input() pageSize: number = 10;
  @Input() showPagination!: boolean;
  @Input() gridId: string = '';
  @Input() cellActionArray: CellActionArray[] = [];
  @Input() templateClass: string = '';
  @Input() refreshGrid$: Observable<CriteriaQuery> = of(new CriteriaQuery());
  @Input() ngTemplateName!: string;
  @Input() interceptors : Map<'postFindall',any> = new Map<'postFindall',any>();
  @Input() multiRowSelect: boolean = true;
  @Input() rowActionArray: RowActionArray[] = [];
  @Output() handleActions: EventEmitter<{ name: string; data: any }> =
    new EventEmitter<{ name: string; data: any }>();
  @ContentChild(TemplateRef) template!: TemplateRef<any>;
  protected _selectedData: any;
  protected selectedRowId: string = '';
  tableData: any[] = [];
  criteriaQuery = new CriteriaQuery();
  searchFormGroup!: FormGroup;
  offset: number = 0;
  pageCount: number = 1;
  loading: boolean = true;
  visiblePages: number[] = [];
  totalPages: number = 0;
  destroy$: Subject<any> = new Subject();
  private _filterColumn: Array<{ type: 'String' | 'Date' | 'Number', key: string }> = []
  @Input('initialCriteria') initialCriteria; 

  constructor(private _roGridService: RoGridService,
    private _fromControlTemplateService: FormControlTemplatesService) { }

  getTemplate(ngTemplateName: string): TemplateRef<any> | null {
    let tmplt = this._fromControlTemplateService.getTemplate(ngTemplateName, 'displayGrid');
    return tmplt;
  }
  ngOnInit(): void {

    this.searchFormGroup = new FormGroup({});
    this._setReadOnlyGridDependency();
    for (let i = 0; i < this.displaycolumndefs.length; i++) {
      if (this.displaycolumndefs[i].columnType) {
        this.searchFormGroup.addControl(
          `${this.displaycolumndefs[i].columnDef}`,
          new FormControl(
            {
              condition: this.displaycolumndefs[i].searchType,
              filter: this.displaycolumndefs[i].searchText,
              sort: this.displaycolumndefs[i].sortType,
            } ?? {}
          )
        );
        this.searchFormGroup
          .get(this.displaycolumndefs[i].columnDef)
          ?.valueChanges.subscribe({
            next: (res: {
              condition: SearchType;
              filter: string;
              sort: SortType;
            }) => {
              (this.search as any)[
                /** Hardcoded search need to replace with dynmanic  */
                `${this.displaycolumndefs[i].columnType}_Search`
              ](i, res);
              console.log(this.criteriaQuery.criteriaQuery());
              this._getGridData();
            },
          });
      }
    }

    this.searchFormGroup.addControl('templateSearch', new FormControl(''));
    this._templateSearch();

    this._roGridService.refreshGrid$.pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (id: string) => {
        if (this.gridId === id) {
          this._selectedData = null;
          this.pageCount = 1;
          this.offset = 0;
          this.criteriaQuery?.setPaginationCriteria(String(0), this.pageSize);
          this._getGridData();
        }
      },
      error: () => {},
      complete: () => {},
    });

    this.refreshGrid$.pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next : (criteria: CriteriaQuery) => {
        this._selectedData = null;
        this.pageCount = 1;
        this.offset = 0;
        if(this.initialCriteria){
          this.criteriaQuery = this.initialCriteria
        }else{
          this.criteriaQuery = criteria || new CriteriaQuery() ;

        }
        this.criteriaQuery?.setPaginationCriteria(String(0), this.pageSize);
        this._getGridData();
      },
      error : () => {

      },
      complete : () => {

      }
    })
  }

  ngOnDestroy(): void {
    this.destroy$.next(null);
    this.destroy$.complete();
  }

  protected selectPage(page: number): void {
    this.pageCount = page;
  }

  private updateVisiblePages(): void {
    const length = Math.min(this.totalPages, this.pageSize);
    const startIndex = Math.max(
      Math.min(
        this.pageCount - Math.ceil(length / 2),
        this.totalPages - length
      ),
      0
    );
    this.visiblePages = Array.from(
      new Array(length).keys(),
      (item) => item + startIndex + 1
    );
  }

  private stringSearch = (
    i: number,
    res: {
      condition: SearchType;
      filter: string;
      sort: SortType;
    }
  ) => {
    if (this.displaycolumndefs[i].searchType) {
      this.criteriaQuery.addFilterCritertia(
        this.displaycolumndefs[i].columnDef,
        this.displaycolumndefs[i].columnType ?? 'String',
        res.condition,
        {
          searchText: res.filter,
        }
      );
    }
    if (this.displaycolumndefs[i].sortType) {
      this.criteriaQuery.addSortCriteria(
        this.displaycolumndefs[i].columnDef,
        res.sort,
        this.displaycolumndefs[i].columnType ?? 'String'
      );
    }
  };

  private stringRange = (
    i: number,
    res: {
      condition: SearchType;
      filter: string;
      sort: SortType;
    }
  ) => {};

  private dateSearch = (
    i: number,
    res: {
      condition: SearchType;
      filter: string;
      sort: SortType;
    }
  ) => {};

  private dateRange = (
    i: number,
    res: {
      condition: SearchType;
      filter: string;
      sort: SortType;
    }
  ) => {};

  private numericSearch = (
    i: number,
    res: {
      condition: SearchType;
      filter: string;
      sort: SortType;
    }
  ) => {};

  private numberRange = (
    i: number,
    res: {
      condition: SearchType;
      filter: string;
      sort: SortType;
    }
  ) => {};

  search = {
    String_Search: this.stringSearch,
    String_Range: this.stringRange,
    Date_Search: this.dateSearch,
    Date_Range: this.dateRange,
    Numeric_Search: this.numericSearch,
    Numeric_Range: this.numberRange,
  };

  private _setReadOnlyGridDependency() {
    this.displaycolumndefs = this.columnsArray.map((item) => {
      if (item.columnType === 'String' && item.searchType) {
        this.criteriaQuery.addFilterCritertia(
          item.columnDef,
          item.columnType,
          item.searchType,
          {
            searchText: item.searchText,
          }
        );
        this._filterColumn.push({ type: 'String', key: item.columnDef })
      } else if (item.columnType === 'Date' && item.searchType) {
        this.criteriaQuery.addFilterCritertia(
          item.columnDef,
          item.columnType,
          item.searchType,
          {
            fromValue: item.fromValue,
            toValue: item.toValue,
          }
        );
        // this._filterColumn.push({ type : 'Date' , key : item.columnDef })
      }
      if (item.columnType && item.sortType) {
        this.criteriaQuery.addSortCriteria(
          item.columnDef,
          item.sortType,
          item.columnType
        );
      }

      return new ColumnDefinition(
        item.action,
        item.columnDef,
        item.header,
        item.cell,
        item.width,
        item.columnType,
        item.searchType,
        item.sortType,
        item.searchText,
        item.fromValue,
        item.toValue
      );
    });
    this.criteriaQuery.setPaginationCriteria('0', this.pageSize);
    this.displayedColumns = [...this.columnsArray.map((c: any) => c.columnDef)];
  }

  private _getGridData(mode?: string) {
    this.loading = true;
    this.tableDatafn
      ?.findAll?.(this.criteriaQuery)()
      .pipe(
        map((res) => {
          if (this.interceptors.has('postFindall')) {
            return this.interceptors.get('postFindall')(res)
          } else {
            return res
          }
        })
      )
      .subscribe({
        next: (res: any) => {
          this.loading = false;
          if (res && res.data && res.data.length > 0) {
            this.tableData = res.data.map((item: any, index: number) => {
              return { id: this.getUniqueId(), ...item };
            });
          } else if (res && res.length > 0) {
            if(mode == 'push') {
              res.map((item: any, index: number) => {
                return { id: this.getUniqueId(), ...item };
              }).forEach(element => {
                this.tableData.push(element);
              });
            }
            else this.tableData = res.map((item: any, index: number) => {
              return { id: this.getUniqueId(), ...item };
            });

          } 
          else if(Array.isArray(res) && res.length == 0) {

          }
          else {
            this.tableData = [];
          }
          if(this.tableData.length>0){
            this._selectedData = []
            this.tableData.forEach((item,index)=>{
              if(item?.isAlreadySelected){
                let data = {...item,index:index}
                this._selectedData.push(data)
              }
            })
          }
          this.totalPages = res?.totalRowCount
            ? Math.ceil(res.totalRowCount / 10)
            : Math.ceil(this.tableData.length / 10);
          this.updateVisiblePages();
          this.mattabledataSource = new MatTableDataSource<any>(this.tableData);
          this.mattabledataSource.filterPredicate = this._filterPredicate;
        },
        error: (err) => {
          this.loading = false;
          console.error(err);
          this.tableData = [];
          this.totalPages = 0;
          this.updateVisiblePages();
          this.mattabledataSource = new MatTableDataSource<any>(this.tableData);
        },
        complete: () => { },
      });
  }


  private _filterPredicate = (data: any, filter: string): boolean => {
    return this._filterColumn.some((keys) => {
      if (keys.type === "String") {
        return data[keys.key].toLowerCase().includes(filter?.toLowerCase());
      }
    });
  };

  onDoubleClick(event: MouseEvent, rowData: any) {
    this.handleActions.emit({
      name: 'dbclick',
      data: rowData,
    });
  }

  getUniqueId(): string {
    return (((1 + Math.random()) * 0x10000) | 0).toString(16).substring(1);
  }

  onClick(event: MouseEvent, rowData: any): void {
    this.selectedRowId = rowData.id;
    this._selectedData = rowData;
    this.handleActions.emit({
      name: 'onclick',
      data: rowData,
    });
  }

  onToolBarClick(action: string): void {
    if (action) {
      this.handleActions.emit({
        name: action,
        data: this._selectedData,
      });
    }
  }

  onBack(pageCount: number) {
    this.offset = this.offset - this.pageSize;
    this.pageCount = this.pageCount - 1;
    this.criteriaQuery.setPaginationCriteria(
      String(this.offset),
      this.pageSize
    );
    this._getGridData();
  }

  onNext(pageCount: number) {
    this.offset = this.offset + this.pageSize;
    this.pageCount = this.pageCount + 1;
    this.criteriaQuery.setPaginationCriteria(
      String(this.offset),
      this.pageSize
    );
    this._getGridData();
  }

  onFirstPage() {
    this.pageCount = 1;
    this.offset = 1;
    this.criteriaQuery.setPaginationCriteria(String(0), this.pageSize);
    this._getGridData();
  }

  onPageIndex(pageIndex: number) {
    this.pageCount = pageIndex;
    this.offset = pageIndex * 10 - 9;
    this.criteriaQuery.setPaginationCriteria(
      String(this.offset),
      this.pageSize
    );
    this._getGridData();
  }

  onLastPage() {
    this.pageCount = this.visiblePages.length;
    this.offset = this.pageCount * 10 - 9;
    this.criteriaQuery.setPaginationCriteria(
      String(this.offset),
      this.pageSize
    );
    this._getGridData();
  }

  onScroll() {
    console.log('scrolled done');

    this.offset = this.offset + this.pageSize;
    this.pageCount = this.pageSize;
    this.criteriaQuery.setPaginationCriteria(
      String(this.offset),
      this.pageCount
    );
    this._getGridData('push');
  }

  private _templateSearch() {
    this.searchFormGroup
      .get("templateSearch")
      ?.valueChanges.pipe(debounceTime(500))
      .subscribe({
        next: (value: any) => {
          this.mattabledataSource.filter = value;
        },
      });
  }

  boxClick(item: any, index: number) {
    if(this.multiRowSelect){
      if (
        this._selectedData &&
        this._selectedData.some((item: any) => item.index === index)
      ) {
        this._selectedData = this._selectedData.filter((item: any) => item.index !== index);
      } else if (this._selectedData &&
        !this._selectedData.some((item: any) => item.index === index)) {
        this._selectedData.push({ ...item, index: index })
      } else {
        this._selectedData = [];
        this._selectedData.push({ ...item, index: index });
      }
    } else {
      this._selectedData = item;
    }
    
    this.handleActions.emit({ name: "onclick", data: this._selectedData });
  }

  rowActionClick(item: any, index: number, action:string){
    this._selectedData = item;
    this.handleActions.emit({
      name: action,
      data: this._selectedData,
    });
  }

  onboxDoubleClick(item: any, index: number) {
    if(this.multiRowSelect){
    if (
      this._selectedData &&
      this._selectedData.some((item: any) => item.index === index)
    ) {
      this._selectedData = this._selectedData.filter((item: any) => item.index !== index);
    } else if (this._selectedData &&
      !this._selectedData.some((item: any) => item.index === index)) {
      this._selectedData.push({ ...item, index: index })
    } else {
      this._selectedData = [];
      this._selectedData.push({ ...item, index: index });
    }
  }else {
    this._selectedData = item;
  }
  this.handleActions.emit({ name: "onDoubleClick", data: this._selectedData });
}
}


export class ColumnDefinition {
  showSearchPopup: boolean = false;
  action: 'sort' | 'search' | 'sort&search' | undefined;
  columnDef: string;
  header: string;
  cell: any;
  width?: number | string;
  columnType: ColumnType | undefined;
  searchType: SearchType | undefined;
  sortType: SortType | undefined;
  searchText?: string | string[];
  fromValue?: string;
  toValue?: string;

  constructor(
    _action: 'sort' | 'search' | 'sort&search' | undefined,
    _columnDef: string,
    _header: string,
    _cell: any,
    _width: number | string,
    _columnType: ColumnType | undefined,
    _searchType: SearchType | undefined,
    _sortType: SortType | undefined,
    _searchText?: string | string[],
    _fromValue?: string,
    _toValue?: string
  ) {
    this.columnDef = _columnDef;
    this.header = _header;
    this.cell = _cell;
    this.width = _width;
    this.columnType = _columnType;
    this.searchType = _searchType;
    this.searchText = _searchText;
    this.fromValue = _fromValue;
    this.toValue = _toValue;
    this.sortType = _sortType;
    this.action = _action;
  }
}
